import classNames from 'classnames';
import type { SharedComponentProps } from '../types';
import * as styles from './index.css';

/**
 * Freeform CSS width, e.g. `120` (px) or `'60%'`. Omit to fill the parent.
 *
 * A percentage needs the parent to have a definite width. Placeholders have no
 * content of their own, so any shrink-to-fit ancestor — a flex row item, an
 * `align-items: center` column — collapses to zero and takes the percentage with it.
 * Prefer an absolute length unless the parent's width is known to be definite.
 */
type SkeletonWidth = number | string;

type SkeletonLineProps = SharedComponentProps<'div'> & {
    width?: SkeletonWidth;
    /** Height, matching one of the theme's font sizes. @default 'base' */
    height?: styles.LineHeight;
};

function Line({ width, height = 'base', className, style, ...props }: SkeletonLineProps) {
    return (
        <span
            className={classNames(styles.line, styles.heightVariants[height], className)}
            style={{ width, ...style }}
            {...props}
        />
    );
}

type SkeletonBoxProps = SharedComponentProps<'div'> & {
    width?: SkeletonWidth;
    /** Freeform CSS height, e.g. `100` (px), `'100px'`, or `'20rem'`. */
    height?: number | string;
};

function Box({ width, height, className, style, ...props }: SkeletonBoxProps) {
    return <div className={classNames(styles.box, className)} style={{ width, height, ...style }} {...props} />;
}

type SkeletonCircleProps = SharedComponentProps<'div'> & {
    /** Diameter, applied to both width and height, e.g. `40` (px) or `'2.5rem'`. */
    radius?: number | string;
};

function Circle({ radius, className, style, ...props }: SkeletonCircleProps) {
    return (
        <div
            className={classNames(styles.circle, className)}
            style={{ width: radius, height: radius, ...style }}
            {...props}
        />
    );
}

type SkeletonOverlayProps = SharedComponentProps<'div'> & {
    /** Whether the overlay is shown over `children`. */
    active: boolean;
};

/** Wraps `children`, drawing a pulsing overlay over them while `active`. */
function Overlay({ active, children, className, style, ...props }: SkeletonOverlayProps) {
    return (
        <div className={classNames({ [styles.overlay]: active }, className)} style={style} {...props}>
            {children}
        </div>
    );
}

export const Skeleton = { Line, Box, Circle, Overlay };
